import React, { useMemo } from 'react';
import { Icon, TooltipBodyDirection, TooltipPosition } from 'venn-ui-kit';
import { useHasFF } from 'venn-utils';
import LayeredProxyInfo from '../modals/proxy-cell/LayeredProxyInfo';
import type { ProxyTriggerProps, ProxySelectorProps } from '../modals/proxy-cell/ProxySelector';
import ProxySelector from '../modals/proxy-cell/ProxySelector';
import { YellowButtonContent, YellowButtonIcon, YellowButtonProxyName } from './YellowButtonStyles';

type LayeredProxyIconButtonProps = Omit<ProxySelectorProps, 'children' | 'proxy'>;

const DefaultTooltip = ({ hasExtrapolationFF }: { hasExtrapolationFF: boolean }) => (
  <div>
    <h4>
      <strong>Proxy Returns Data</strong>
    </h4>
    <div>
      {`Backfill, substitute, desmooth,${
        hasExtrapolationFF ? ' extrapolate' : ''
      } or interpolate your data with an investment with similar returns.`}
    </div>
  </div>
);

const LayeredProxyIconButton = ({
  dropdownVerticalDirection,
  investment,
  loading,
  readOnly,
  usePortal,
  onProxyChange,
}: LayeredProxyIconButtonProps) => {
  const hasExtrapolationFF = useHasFF('extrapolation_ff');
  const { proxyInfos = [] } = investment;
  const selectedProxyName = proxyInfos.length > 1 ? 'Multiple...' : proxyInfos[0]?.proxyName;

  const tooltipContent = useMemo(
    () =>
      selectedProxyName ? (
        <LayeredProxyInfo key={investment.id} investment={investment} />
      ) : (
        <DefaultTooltip hasExtrapolationFF={hasExtrapolationFF} />
      ),
    [selectedProxyName, investment, hasExtrapolationFF],
  );

  return (
    <ProxySelector
      dropdownVerticalDirection={dropdownVerticalDirection}
      hoverAddProxy={false}
      investment={investment}
      portalLeftOffset={82}
      portalTopOffset={-5}
      proxy={null}
      readOnly={readOnly}
      usePortal={usePortal}
      loading={loading}
      onProxyChange={onProxyChange}
    >
      {({ selectorOpen, onToggleSelector, isProxyUpdating }: ProxyTriggerProps) => (
        <div className="ml-[10px]">
          <YellowButtonIcon
            active={selectorOpen}
            border
            className={selectedProxyName ? 'qa-proxy-name qa-edit-label' : undefined}
            customIcon={
              <YellowButtonContent>
                <Icon type="arrow-from-right" />
                {selectedProxyName && (
                  <YellowButtonProxyName disabled={isProxyUpdating || loading}>
                    {selectedProxyName}
                  </YellowButtonProxyName>
                )}
              </YellowButtonContent>
            }
            disabled={isProxyUpdating || loading || readOnly}
            tooltip={tooltipContent}
            tooltipDirection={TooltipBodyDirection.Left}
            tooltipMaxWidth={300}
            tooltipPosition={selectedProxyName ? TooltipPosition.Bottom : TooltipPosition.Left}
            usePortal={usePortal}
            onClick={readOnly ? undefined : onToggleSelector}
          />
        </div>
      )}
    </ProxySelector>
  );
};

export default LayeredProxyIconButton;
